import { Warning } from "@mui/icons-material";
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import React from "react";
import { useCurrencyFormatter } from "../hooks/useCurrencyFormatter";
import { useModeMetrics } from "../mode/useModeMetrics";
import { useMode } from "../mode/ModeContext";
import { modes } from "../mode/modes";
import MortgageSimulator from "../simulator";
import Insights from "./Insights";
import SimulationMenu from "./SimulationMenu";
import SimulationChartMini from "./SimulationChartMini";

export default function SimulationCard({
  sim,
  moreExpensive,
  idx,
  handleEdit,
  handleDuplicate,
  handleDelete,
  toggleExpanded,
  deleteDisabled = false,
}: {
  sim: MortgageSimulator;
  moreExpensive: number | null;
  idx: number;
  handleEdit?: (idx: number) => void;
  handleDuplicate?: (idx: number) => void;
  handleDelete?: (idx: number) => void;
  toggleExpanded: () => void;
  deleteDisabled?: boolean;
}) {
  const c = useCurrencyFormatter();
  const { mode } = useMode();
  const { Primary: PrimaryMetric, Secondary: SecondaryMetric } =
    useModeMetrics();

  const hideSecondaryInCard = mode
    ? modes[mode].hideSecondaryMetricInCard
    : false;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <CardHeader
        action={
          <>
            {sim.warningMessages.length > 0 && (
              <Tooltip title={sim.warningMessages.join("\n")} arrow>
                <IconButton size="large" color="warning">
                  <Warning />
                </IconButton>
              </Tooltip>
            )}
            <SimulationMenu
              idx={idx}
              handleEdit={handleEdit}
              handleDuplicate={handleDuplicate}
              handleDelete={handleDelete}
              deleteDisabled={deleteDisabled}
            />
          </>
        }
        title={<PrimaryMetric sim={sim} />}
        subheader={
          !hideSecondaryInCard ? <SecondaryMetric sim={sim} /> : undefined
        }
        slotProps={{
          title: { sx: { fontWeight: 600 } },
        }}
      />
      <CardContent sx={{ pt: 0, pb: 0 }}>
        <Box>
          <SimulationChartMini sim={sim} />
        </Box>
        {(moreExpensive !== null || sim.additionalCapitalRepayments) && (
          <Box sx={{ pt: 2 }}>
            <Insights moreExpensive={moreExpensive} sim={sim} />
          </Box>
        )}
      </CardContent>
      <CardActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          backgroundColor: "background.default",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Total Cost:{" "}
          <Tooltip
            title={`${c(sim.principal, true)} principal paid + ${c(sim.totalInterestPaid, true)} mortgage interest paid${sim.totalInterestEarned > 0 ? ` - ${c(sim.totalInterestEarned, true)} savings & investments interest earned` : ""}`}
            arrow
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, -5],
                    },
                  },
                ],
              },
            }}
          >
            <Typography
              component="span"
              variant="body2"
              fontWeight="600"
              color="text.primary"
            >
              {c(sim.totalCostOfMortgage, true)}
            </Typography>
          </Tooltip>
        </Typography>
        <Button size="small" onClick={() => toggleExpanded()}>
          View Details
        </Button>
      </CardActions>
    </Card>
  );
}
